/**
 * NaturalitySquaresGamePanel — THE GAME.
 *
 * Complete commuting squares by arming a bottom rewrite and placing it. The
 * reducer accepts only the candidate whose naturality square commutes; every
 * non-natural 2-cell has no slot and never advances the round.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */
import React, { useCallback, useMemo, useState } from "react";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
import {
  ArcadeActionButton,
  ArcadeArenaCard,
  ArcadeArenaStage,
  ArcadeBlockedSlot,
  ArcadeKeyframes,
  ArcadeLogPanel,
  ArcadeMissionPrompt,
  ArcadePlayfieldFrame,
  ArcadeRunControls,
  ArcadeStatusDots,
  FlashOverlay,
  arcadeRankColor,
  createArcadeHudStripProps,
  timeBonus,
  useArcadeAutoClock,
  useArcadeClock,
  useArcadeHotkeys,
  useArcadeScore,
  useReducedMotion,
  type ArcadeHotkeyBinding,
} from "../../../games/index.js";
import levelsData from "../data/naturalitySquaresLevels.json" with { type: "json" };
import {
  createSquaresState,
  currentSquare,
  placementIsNatural,
  squaresProgress,
  squaresReducer,
  type NaturalityLevel,
  type SquaresState,
} from "../algorithms/naturalitySquares.js";
import { NaturalitySquareBoard } from "./naturality-squares-panel/NaturalitySquareBoard.js";

const LEVELS = levelsData.levels as unknown as readonly [
  NaturalityLevel,
  ...NaturalityLevel[],
];
const ACCENT = panelTokens.accentViolet;
const DANGER = panelTokens.accentRed;
const ROUND_SECONDS = 36;

export function NaturalitySquaresGamePanel(): React.JSX.Element {
  const reducedMotion = useReducedMotion();
  const [levelIndex, setLevelIndex] = useState(0);
  const level = LEVELS[levelIndex] ?? LEVELS[0];
  const [state, setState] = useState<SquaresState>(() =>
    createSquaresState(level),
  );
  const [armedCandidateId, setArmedCandidateId] = useState<string | null>(null);
  const arcade = useArcadeScore({ hitColor: ACCENT, missColor: DANGER });
  const clock = useArcadeClock(ROUND_SECONDS, () => {
    arcade.miss(DANGER);
  });
  const { pause: pauseClock, reset: resetClock, start: startClock } = clock;

  const square = currentSquare(level, state);
  const candidates = square?.candidates ?? [];
  const armedCandidate =
    candidates.find((candidate) => candidate.id === armedCandidateId) ?? null;
  const isOver = state.status !== "playing";
  const isLast = levelIndex === LEVELS.length - 1;
  const progress = Math.round(squaresProgress(level, state) * 100);

  const startLevel = useCallback(
    (index: number): void => {
      const target = LEVELS[index] ?? LEVELS[0];
      setLevelIndex(index);
      setState(createSquaresState(target));
      setArmedCandidateId(null);
      arcade.reset();
      resetClock(ROUND_SECONDS);
      startClock();
    },
    [arcade, resetClock, startClock],
  );

  const reset = useCallback(() => {
    startLevel(levelIndex);
  }, [levelIndex, startLevel]);

  const nextLevel = useCallback(() => {
    startLevel(Math.min(LEVELS.length - 1, levelIndex + 1));
  }, [levelIndex, startLevel]);

  const placeCandidate = useCallback(
    (candidateId: string): void => {
      if (state.status !== "playing") return;
      const active = currentSquare(level, state);
      if (active === null) return;
      const natural = placementIsNatural(active, candidateId);
      const next = squaresReducer(level, state, {
        kind: "place",
        candidateId,
      });
      setState(next);
      setArmedCandidateId(null);
      if (natural) {
        arcade.hit({
          bonus: next.status === "cleared" ? timeBonus(clock.remaining) : 0,
        });
        if (next.status === "cleared") pauseClock();
      } else {
        arcade.miss();
      }
    },
    [arcade, clock.remaining, level, pauseClock, state],
  );

  const placeArmedCandidate = useCallback((): void => {
    if (armedCandidate !== null) placeCandidate(armedCandidate.id);
  }, [armedCandidate, placeCandidate]);

  const cycleCandidate = useCallback(
    (direction: -1 | 1): void => {
      if (candidates.length === 0 || isOver) return;
      const currentIndex =
        armedCandidateId === null
          ? direction > 0
            ? -1
            : 0
          : candidates.findIndex(
              (candidate) => candidate.id === armedCandidateId,
            );
      const baseIndex =
        currentIndex >= 0 ? currentIndex : direction > 0 ? -1 : 0;
      const nextIndex =
        (baseIndex + direction + candidates.length) % candidates.length;
      setArmedCandidateId(candidates[nextIndex].id);
    },
    [armedCandidateId, candidates, isOver],
  );

  const hotkeys = useMemo((): readonly ArcadeHotkeyBinding[] => {
    const bindings: ArcadeHotkeyBinding[] = [
      {
        key: "ArrowLeft",
        enabled: !isOver,
        onTrigger: (): void => {
          cycleCandidate(-1);
        },
      },
      {
        key: "ArrowRight",
        enabled: !isOver,
        onTrigger: (): void => {
          cycleCandidate(1);
        },
      },
      {
        code: "Space",
        enabled: !isOver && armedCandidate !== null,
        onTrigger: placeArmedCandidate,
      },
      {
        key: "Enter",
        enabled: !isOver && armedCandidate !== null,
        onTrigger: placeArmedCandidate,
      },
      { key: "r", onTrigger: reset },
    ];
    candidates.slice(0, 9).forEach((candidate, index) => {
      bindings.push({
        key: String(index + 1),
        enabled: !isOver,
        onTrigger: (): void => {
          setArmedCandidateId(candidate.id);
        },
      });
    });
    return bindings;
  }, [
    armedCandidate,
    candidates,
    cycleCandidate,
    isOver,
    placeArmedCandidate,
    reset,
  ]);
  useArcadeHotkeys(hotkeys);
  useArcadeAutoClock({
    active: state.status === "playing",
    onPause: pauseClock,
    onStart: startClock,
  });

  const statusMessage = useMemo((): string => {
    if (state.status === "cleared")
      return `Every square commuted. Rank ${arcade.rank}.`;
    if (state.lastEvent === "no-slot")
      return "That bottom rewrite does not commute. A non-natural 2-cell has no slot here.";
    if (armedCandidate !== null)
      return `Rewrite ${armedCandidate.label} is armed. Place it only if both composite paths agree.`;
    return "Arm a bottom rewrite, then place it if the square commutes.";
  }, [armedCandidate, state.lastEvent, state.status, arcade.rank]);

  return (
    <PanelSurface.PanelShell
      title="Naturality Squares"
      subtitle="Play · arm the bottom rewrite that makes the square commute"
      accentColor={ACCENT}
    >
      <ArcadeKeyframes />
      <ArcadeArenaCard glowColor={`${ACCENT}30`}>
        <div className="relative overflow-hidden rounded-2xl">
          <ArcadeArenaStage
            hudOverlay={
              <FlashOverlay
                token={arcade.flashToken}
                color={arcade.flashColor}
                reducedMotion={reducedMotion}
              />
            }
            hudProps={createArcadeHudStripProps({
              stats: [
                {
                  label: "solved",
                  value: `${state.solved}/${level.squares.length}`,
                  color: ACCENT,
                },
                {
                  label: "square",
                  value:
                    square === null
                      ? "clear"
                      : `${state.squareIndex + 1}/${level.squares.length}`,
                  color: panelTokens.accentCyan,
                },
                {
                  label: "armed",
                  value: armedCandidate?.label ?? "none",
                  color:
                    armedCandidate === null ? panelTokens.muted : ACCENT,
                },
                {
                  label: "progress",
                  value: `${progress}%`,
                  color: panelTokens.accentGreen,
                },
                {
                  label: "non-natural",
                  value: "off",
                  color: DANGER,
                },
              ],
              score: arcade.score,
              rank: arcade.rank,
              rankColor: arcadeRankColor(arcade.rank),
              scoreColor: panelTokens.bright,
              borderColor: panelTokens.border,
              clock,
              timerAccent: ACCENT,
              timerDanger: DANGER,
              timerTrack: "rgba(148,163,184,0.18)",
              combo: arcade.combo,
              comboColor: ACCENT,
              comboMuted: panelTokens.muted,
              reducedMotion,
            })}
            levelSelectProps={{
              levels: LEVELS,
              activeLevelId: level.id,
              onSelect: (levelId) => {
                startLevel(
                  Math.max(
                    0,
                    LEVELS.findIndex((candidate) => candidate.id === levelId),
                  ),
                );
              },
              onReset: reset,
              accentColor: ACCENT,
              borderColor: panelTokens.border,
              mutedColor: panelTokens.muted,
              brightColor: panelTokens.bright,
              activeBackground: `${ACCENT}22`,
              background: "rgba(5,8,16,0.44)",
            }}
          >
            <ArcadePlayfieldFrame
              borderColor={panelTokens.border}
              background="rgba(5,8,16,0.9)"
              accentColor={ACCENT}
              secondaryColor={panelTokens.accentCyan}
              gridSize={44}
              minHeight={560}
              header={
                <PanelSurface.SectionHeading>
                  Commuting square
                </PanelSurface.SectionHeading>
              }
            >
              <ArcadeMissionPrompt
                label="bottom rewrite"
                title={isOver ? "Round cleared" : "Make the square commute"}
                detail={level.brief}
                color={isOver ? panelTokens.accentGreen : ACCENT}
                status={
                  armedCandidate === null
                    ? "arm rewrite"
                    : `armed ${armedCandidate.label}`
                }
              />
              <NaturalitySquareBoard
                armedCandidateId={armedCandidateId}
                disabled={isOver}
                onArmCandidate={setArmedCandidateId}
                square={square}
              />
              <ArcadeBlockedSlot
                label="no slot"
                title="Non-natural rewrite"
                detail="A boundary-crossing 2-cell never commutes here, so the placement has no commuting slot."
                color={DANGER}
                className="mt-4"
              />
            </ArcadePlayfieldFrame>

            <ArcadeLogPanel
              title="Naturality console"
              event={statusMessage}
              borderColor={panelTokens.border}
              background="rgba(5,8,16,0.68)"
              textColor={panelTokens.text}
              badges={[
                PanelSurface.badge("2-cell", ACCENT),
                PanelSurface.badge("commuting square", panelTokens.accentCyan),
              ]}
            >
              <ArcadeStatusDots
                items={[
                  { label: "rewrite armed", active: armedCandidate !== null },
                  { label: "square solved", active: state.lastEvent === "commuted" },
                  { label: "round clear", active: state.status === "cleared" },
                  { label: "no non-natural slot", active: true },
                ]}
                activeColor={panelTokens.accentGreen}
                inactiveColor={DANGER}
                activeText="ok"
                inactiveText="no"
              />
              <div className="mt-4 grid gap-2">
                <ArcadeRunControls
                  clockRunning={clock.running}
                  disabledPrimary={isOver || armedCandidate === null}
                  onPrimary={placeArmedCandidate}
                  onReset={reset}
                  onToggleClock={() => {
                    if (clock.running) pauseClock();
                    else startClock();
                  }}
                  primaryAriaLabel="Place armed bottom rewrite"
                  primaryColor={ACCENT}
                  primaryIcon=">"
                  primaryLabel={
                    armedCandidate === null
                      ? "Arm rewrite"
                      : `Place ${armedCandidate.label}`
                  }
                  resetAriaLabel="Reset Naturality Squares"
                  resetColor={DANGER}
                  toggleColor={panelTokens.accentCyan}
                />
                {state.status === "cleared" && !isLast ? (
                  <ArcadeActionButton
                    color={ACCENT}
                    onClick={nextLevel}
                    ariaLabel="Next naturality level"
                  >
                    Next Level
                  </ArcadeActionButton>
                ) : null}
              </div>
            </ArcadeLogPanel>
          </ArcadeArenaStage>
        </div>
      </ArcadeArenaCard>

      {state.status === "cleared" ? (
        <PanelSurface.Callout
          tone="safe"
          title="Every 2-cell was natural by construction"
        >
          You completed each square so it commutes. A non-natural,
          boundary-crossing 2-cell never had a slot to place, so it was
          unconstructable throughout.
        </PanelSurface.Callout>
      ) : null}
    </PanelSurface.PanelShell>
  );
}
